<template>
  <div 
    class="tw-px-3 tw-py-2.5 hover:tw-bg-gray-50 tw-transition-colors tw-cursor-pointer"
    :class="{ 'tw-bg-primary-50 hover:tw-bg-primary-100': notification.unread && !isCompleted }"
    @click="handleNotificationClick"
  >
    <div class="tw-flex tw-items-center tw-gap-2">
      <!-- User Image / Icon -->
      <div class="tw-w-12 tw-h-12 tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-indigo-100 tw-border tw-border-solid tw-border-indigo-200 tw-overflow-hidden">
        <img 
          v-if="meetingData?.organizer?.profile_image && !imageError" 
          :src="meetingData.organizer.profile_image" 
          :alt="meetingData.organizer.name"
          class="tw-w-full tw-h-full tw-object-cover"
          @error="handleImageError"
        />
        <icon v-else icon="solar:calendar-add-bold" class="tw-text-indigo-600 tw-text-xl" />
      </div>
      
      <!-- Content -->
      <div class="tw-flex-1 tw-min-w-0">
        <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
          <div class="tw-flex-1 tw-min-w-0">
            <p class="tw-text-sm tw-font-medium tw-text-gray-800">
              <span v-if="!isCompleted">{{ notification.title }}</span>
              <span v-else-if="completedAction === 'accept'">{{ meetingData?.title || 'Meeting' }}</span>
              <span v-else-if="completedAction === 'decline'">{{ meetingData?.title || 'Meeting' }}</span>
            </p>
            <p v-if="!isCompleted && meetingData?.title" class="tw-text-xs tw-text-gray-500 tw-mt-0.5 tw-break-words">
              {{ meetingData.title }}
            </p>
          </div>
          <span class="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap tw-shrink-0">{{ formatTime(notification.created_at || notification.time) }}</span>
        </div>
        
        <!-- Meeting details preview -->
        <div v-if="meetingData" class="tw-mt-1.5 tw-space-y-1">
          <div v-if="meetingData.scheduled_at" class="tw-text-xs tw-text-indigo-600 tw-flex tw-items-center tw-gap-1">
            <icon icon="solar:calendar-bold-duotone" class="tw-shrink-0" />
            <span class="tw-break-words">{{ formatMeetingDateTime(meetingData.scheduled_at) }}</span>
          </div>
          <div v-if="meetingData.organizer" class="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
            <icon icon="solar:user-bold-duotone" class="tw-shrink-0" />
            <span class="tw-break-words">{{ $t('common.organizer', 'Organizer') }}: {{ meetingData.organizer.name }}</span>
          </div>
          <div v-if="meetingData.location" class="tw-text-xs tw-text-gray-500 tw-flex tw-items-center tw-gap-1">
            <icon icon="solar:map-point-bold-duotone" class="tw-shrink-0" />
            <span class="tw-break-words">{{ meetingData.location }}</span>
          </div>
        </div>
      
        <!-- Action buttons - only show if not completed and status is pending -->
        <div v-if="!isCompleted && meetingData?.status === 'pending'" class="tw-w-full tw-grid tw-grid-cols-3 tw-mt-2 tw-gap-1.5">
          <a-button 
            intent="gradient" 
            full 
            class="!tw-min-h-[0px] tw-h-[25px]"
            :loading="isProcessing && actionType === 'accept'"
            :disabled="isProcessing"
            @click.stop="handleAccept"
          >
            <span class="tw-text-xs">{{ $t('buttons.accept') }}</span>
          </a-button>
          <a-button 
            intent="danger" 
            full 
            class="!tw-min-h-[0px] tw-h-[25px]"
            :loading="isProcessing && actionType === 'decline'"
            :disabled="isProcessing"
            @click.stop="handleDecline"
          >
            <span class="tw-text-xs">{{ $t('buttons.decline') }}</span>
          </a-button>
          <a-button 
            intent="secondary" 
            full 
            class="!tw-min-h-[0px] tw-h-[25px]"
            :disabled="isProcessing"
            @click.stop="handleViewDetails"
          >
            <span class="tw-text-xs">{{ $t('buttons.view-details', 'View') }}</span>
          </a-button>
        </div>
        
        <!-- Completion status - show instead of buttons when completed -->
        <div v-if="isCompleted" class="tw-mt-2 tw-flex tw-items-center tw-gap-1.5">
          <span v-if="completedAction === 'accept'" class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-green-600 tw-bg-green-50 tw-px-2 tw-py-1 tw-rounded-full">
            <icon icon="solar:check-circle-bold" />
            {{ $t('messages.meeting-accepted', 'Accepted') }}
          </span>
          <span v-else-if="completedAction === 'decline'" class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-gray-50 tw-px-2 tw-py-1 tw-rounded-full">
            <icon icon="solar:close-circle-bold" />
            {{ $t('messages.meeting-declined', 'Declined') }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- Meeting Details Popup -->
  <MeetingDetailsPopup 
    :visible="showMeetingPopup"
    :notification="popupNotification"
    @close="closeMeetingPopup"
    @meetingAccepted="handleMeetingAccepted"
    @meetingDeclined="handleMeetingDeclined"
  />
</template>

<script setup>
import { defineProps, defineEmits, ref, computed } from 'vue';
import { useI18n } from "vue-i18n";
import meetingsApi from '@/api/meetings';
import useAlert from '@/composables/useAlert';
import MeetingDetailsPopup from '../modals/MeetingDetailsPopup.vue';

const { t } = useI18n();

// Define props
const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
});

// Define emits
const emit = defineEmits(['notificationRead', 'popupStateChanged']);

// Reactive state management
const isProcessing = ref(false);
const actionType = ref(null); // 'accept' | 'decline' | 'view_details' | null
const showMeetingPopup = ref(false);
const fullMeetingDetails = ref(null);
const imageError = ref(false);

// Extract meeting data from notification
const meetingData = computed(() => {
  return props.notification?.data || null;
});

// Handle image error - fallback to icon
function handleImageError(event) {
  imageError.value = true;
  event.target.style.display = 'none';
}

// Check if already completed based on meeting status
const isCompleted = computed(() => {
  const status = meetingData.value?.status;
  return status === 'accepted' || status === 'declined' || status === 'cancelled' || status === 'completed';
});

const completedAction = computed(() => {
  const status = meetingData.value?.status;
  if (status === 'accepted') return 'accept';
  if (status === 'declined') return 'decline';
  return null;
});

// Handle accept action
async function handleAccept() {
  if (isProcessing.value || isCompleted.value) return;
  
  try {
    isProcessing.value = true;
    actionType.value = 'accept';
    
    // Get meeting ID from notification data  
    const meetingId = meetingData.value?.meeting_id;
    if (!meetingId) {
      throw new Error('Meeting ID not found in notification data');
    }
    
    console.log(`Accepting meeting invitation ${meetingId}...`);
    await meetingsApi.accept(meetingId);
    
    // Update local state
    if (meetingData.value) {
      meetingData.value.status = 'accepted';
    }
    
    // Show success message
    const meetingTitle = meetingData.value?.title || 'meeting';
    useAlert(t('messages.meeting-accepted-success', { title: meetingTitle }, `Meeting "${meetingTitle}" accepted successfully`), 'success');
    
    // Mark notification as read
    if (props.notification.unread) {
      emit('notificationRead', props.notification.id);
    }
    
    console.log(`Successfully accepted meeting invitation: ${meetingTitle}`);
  } catch (error) {
    console.error('Error accepting meeting invitation:', error);
    const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
    useAlert(errorMessage, 'danger');
  } finally {
    isProcessing.value = false;
    actionType.value = null;
  }
}

// Handle decline action
async function handleDecline() {
  if (isProcessing.value || isCompleted.value) return;
  
  try {
    isProcessing.value = true;
    actionType.value = 'decline';
    
    // Get meeting ID from notification data
    const meetingId = meetingData.value?.meeting_id;
    if (!meetingId) {
      throw new Error('Meeting ID not found in notification data');
    }
    
    console.log(`Declining meeting invitation ${meetingId}...`);
    await meetingsApi.decline(meetingId, {
      reason: 'Declined via notification'
    });
    
    // Update local state
    if (meetingData.value) {
      meetingData.value.status = 'declined';
    }
    
    // Show success message
    const meetingTitle = meetingData.value?.title || 'meeting';
    useAlert(t('messages.meeting-declined-success', { title: meetingTitle }, `Meeting "${meetingTitle}" declined`), 'success');
    
    // Mark notification as read
    if (props.notification.unread) {
      emit('notificationRead', props.notification.id);
    }
    
    console.log(`Successfully declined meeting invitation: ${meetingTitle}`);
  } catch (error) {
    console.error('Error declining meeting invitation:', error);
    const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
    useAlert(errorMessage, 'danger');
  } finally {
    isProcessing.value = false;
    actionType.value = null;
  }
}

// Handle notification click (opens popup)
async function handleNotificationClick(event) {
  // Don't open popup if clicking on buttons
  if (event.target.closest('button') || event.target.closest('.tw-grid')) {
    return;
  }
  
  await openMeetingPopup();
}

// Open meeting popup
async function openMeetingPopup() {
  const meetingId = meetingData.value?.meeting_id;
  if (!meetingId) {
    useAlert(t('errors.meeting-id-not-found', 'Meeting ID not found'), 'warning');
    return;
  }
  
  try {
    // Fetch full meeting details
    const response = await meetingsApi.get(meetingId);
    fullMeetingDetails.value = response.data?.data;
    
    // Open popup
    showMeetingPopup.value = true;
    emit('popupStateChanged', true);
    
    // Mark notification as read
    if (props.notification.unread) {
      emit('notificationRead', props.notification.id);
    }
  } catch (error) {
    console.error('Error fetching meeting details:', error);
    const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
    useAlert(errorMessage, 'danger');
  }
}

// Handle view details action
async function handleViewDetails() {
  await openMeetingPopup();
}

// Close meeting popup
function closeMeetingPopup() {
  showMeetingPopup.value = false;
  emit('popupStateChanged', false);
}

// Handle meeting accepted from popup
async function handleMeetingAccepted(meeting) {
  const meetingId = meeting?.id || meetingData.value?.meeting_id;
  if (!meetingId) return;
  
  try {
    await meetingsApi.accept(meetingId);
    
    // Update local state
    if (meetingData.value) {
      meetingData.value.status = 'accepted';
    }
    if (fullMeetingDetails.value) {
      fullMeetingDetails.value.status = 'accepted';
    }
    
    const meetingTitle = meeting?.title || meetingData.value?.title || 'meeting';
    useAlert(t('messages.meeting-accepted-success', { title: meetingTitle }, `Meeting "${meetingTitle}" accepted successfully`), 'success');
    
    closeMeetingPopup();
  } catch (error) {
    console.error('Error accepting meeting from popup:', error);
    const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
    useAlert(errorMessage, 'danger');
  }
}

// Handle meeting declined from popup
async function handleMeetingDeclined(meeting) {
  const meetingId = meeting?.id || meetingData.value?.meeting_id;
  if (!meetingId) return;
  
  try {
    await meetingsApi.decline(meetingId, {
      reason: 'Declined via notification popup'
    });
    
    // Update local state
    if (meetingData.value) {
      meetingData.value.status = 'declined';
    }
    if (fullMeetingDetails.value) {
      fullMeetingDetails.value.status = 'declined';
    }
    
    const meetingTitle = meeting?.title || meetingData.value?.title || 'meeting';
    useAlert(t('messages.meeting-declined-success', { title: meetingTitle }, `Meeting "${meetingTitle}" declined`), 'success');
    
    closeMeetingPopup();
  } catch (error) {
    console.error('Error declining meeting from popup:', error);
    const errorMessage = error.response?.data?.message || error.message || t('errors.something-went-wrong', 'Something went wrong');
    useAlert(errorMessage, 'danger');
  }
}

// Create adapted notification object for popup (MeetingDetailsPopup expects notification.data.meeting)
const popupNotification = computed(() => {
  // Use full meeting details if available, otherwise use notification data
  let meeting = fullMeetingDetails.value || meetingData.value;
  
  if (!meeting) return props.notification;
  
  // Transform participants to include name directly (popup expects participant.name, not participant.user.name)
  const transformedParticipants = meeting.participants?.map(participant => ({
    ...participant,
    name: participant.user?.name || participant.name || 'Unknown'
  })) || [];
  
  // Transform meeting data to match popup expectations
  const transformedMeeting = {
    ...meeting,
    // Map scheduled_at to start_time for popup compatibility
    start_time: meeting.start_time || meeting.scheduled_at,
    // Calculate end_time from scheduled_at + duration_minutes
    end_time: meeting.end_time || (meeting.scheduled_at && meeting.duration_minutes 
      ? new Date(new Date(meeting.scheduled_at).getTime() + meeting.duration_minutes * 60000).toISOString()
      : null),
    // Map room_url to meeting_url for virtual meeting detection
    meeting_url: meeting.meeting_url || meeting.room_url || meeting.host_room_url,
    // Ensure location field exists
    location: meeting.location || null,
    // Transform participants array
    participants: transformedParticipants
  };
  
  return {
    ...props.notification,
    data: {
      ...props.notification.data,
      meeting: transformedMeeting
    }
  };
});

// Format meeting date and time for display
function formatMeetingDateTime(dateString) {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let dateText;
    if (date.toDateString() === today.toDateString()) {
      dateText = t('common.today', 'Today');
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dateText = t('common.tomorrow', 'Tomorrow');
    } else {
      const month = date.toLocaleString(undefined, { month: 'short' });
      const day = date.getDate();
      dateText = `${month} ${day}`;
    }
    
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    const timeText = `${displayHours}:${displayMinutes} ${ampm}`;
    
    return `${dateText} at ${timeText}`;
  } catch (error) {
    console.error('Error formatting meeting date time:', error);
    return '';
  }
}

// Format timestamp to relative time (e.g. "2h ago")
const formatTime = (timestamp) => {
  const now = new Date();
  let date;
  
  // Handle different timestamp formats
  if (typeof timestamp === 'string') {
    date = new Date(timestamp);
  } else if (typeof timestamp === 'number') {
    date = new Date(timestamp * 1000);
  } else if (timestamp instanceof Date) {
    date = timestamp;
  } else {
    date = timestamp?.created_at ? new Date(timestamp.created_at) : new Date();
  }
  
  // Invalid date, use current time
  if (isNaN(date.getTime())) {
    date = new Date();
  }
  
  const diff = now - date;
  
  // Convert to minutes
  const minutes = Math.floor(diff / 60000);
  
  if (minutes < 60) {
    return minutes <= 0 
      ? t('time.just-now', 'Just now')
      : t('time.min-ago', { count: minutes }, `${minutes}m`);
  }
  
  // Convert to hours
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t('time.hour-ago', { count: hours }, `${hours}h`);
  }
  
  // Convert to days
  const days = Math.floor(hours / 24);
  return t('time.day-ago', { count: days }, `${days}d`);
};
</script>

